import { useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FilmIcon, Loader2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Signup() {
  const { user, signup } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await signup({ email, password, name, referralCode });
      toast.success("Welcome — 5 free credits added to your account");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwcmVhbCUyMGVzdGF0ZXxlbnwwfHx8fDE3ODQxODcxOTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury living room"
          className="co-kenburns absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/95 via-black/40 to-transparent" />
        <Link to="/" className="absolute left-12 top-12 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <FilmIcon className="w-5 h-5 text-black" strokeWidth={2} />
          </div>
          <div className="text-xl font-heading font-bold tracking-tight">MediaOthers</div>
        </Link>
        <div className="absolute left-12 bottom-14 max-w-md space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
            <div className="font-mono-tech text-[10px] text-amber-400">FREE · 5 · CREDITS · ON · SIGNUP</div>
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl leading-none tracking-tighter font-light">
            Start generating<br />
            <span className="font-bold text-amber-400">cinematic listings</span>.
          </h1>
          <p className="text-zinc-300 text-base max-w-sm">
            Free preview mode + Kling, Veo, Runway, Luma and Hailuo with credits. Pay-as-you-go, no subscription needed.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="signup-form">
          <div>
            <div className="font-mono-tech text-[10px] text-amber-400 mb-3">CREATE · ACCOUNT</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Sign up</h2>
            <p className="text-zinc-400 text-sm mt-2">5 free credits + 7-day trial available on paid plans.</p>
          </div>

          <div className="space-y-2">
            <Label className="font-mono-tech text-[10px] text-zinc-500">NAME</Label>
            <Input data-testid="signup-name" required value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 h-11" />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-tech text-[10px] text-zinc-500">EMAIL</Label>
            <Input data-testid="signup-email" required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 h-11" />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-tech text-[10px] text-zinc-500">PASSWORD</Label>
            <Input data-testid="signup-password" required type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} minLength={6}
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 h-11" />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-tech text-[10px] text-zinc-500">REFERRAL CODE · OPTIONAL</Label>
            <Input data-testid="signup-referral" value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="MO-XXXXXXXX"
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 h-11 font-mono-tech uppercase" />
          </div>

          <Button type="submit" disabled={busy} data-testid="signup-submit"
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? "Creating account…" : "Create free account"}
          </Button>

          <div className="text-center text-sm text-zinc-400">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-login" className="text-amber-400 hover:text-amber-300 font-medium">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
